import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton, ListItem, styled, Typography } from '@mui/material';
import DragHandle from '@mui/icons-material/DragHandle';
import MoreVert from '@mui/icons-material/MoreVert';
import { addOpacity, titleize } from 'lib/helpers';
import { Row } from 'ui/Flex';
import { MenuOn } from 'ui/MenuOn';

const PREFIX = 'SortableItemRow';

const classes = {
    dragHandle: `${PREFIX}-dragHandle`
};

const StyledListItem = styled(ListItem)(({ theme }) => ({
    backgroundColor: addOpacity(theme.palette.background.paper, 0.9),
    padding: theme.spacing(0.5),
    zIndex: 1300,
    '&:hover': {
        backgroundColor: addOpacity(theme.palette.background.paper, 0.9)
    },
    [`& .${classes.dragHandle}`]: {
        cursor: 'pointer',
        height: '1em',
        width: '1em',
        flexBasis: '5%',
        marginRight: '30px',
        touchAction: 'none'
    }
}));

export interface SortableFieldDefinition {
    width: string;
    label: string;
    key: string;
}

export interface FormComponentProps<Model> {
    value?: Partial<Model>;
    onSubmit: (value: Partial<Model>) => void;
    close: () => void;
    formTitle: string;
}

interface SortableItemRowProps<Model> {
    item: Model;
    id: number;
    index: number;
    onEditItem: (index: number) => void;
    onDeleteItem: (index: number) => void;
    fields: SortableFieldDefinition[];
}

export const SortableItemRow: React.FC<SortableItemRowProps<any>> = props => {
    const { fields, item, onEditItem, onDeleteItem, id, index } = props;
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform ? { ...transform, x: 0 } : null),
        transition
    };

    const menuItems = [
        {
            label: 'Edit',
            onClick: () => {
                onEditItem(index);
            }
        },
        {
            label: 'Delete',
            onClick: () => {
                onDeleteItem(index);
            }
        }
    ];
    const renderOn = React.useCallback(
        (toggle: (event: React.MouseEvent<HTMLButtonElement>) => void) => (
            <IconButton onClick={toggle} size="large">
                <MoreVert />
            </IconButton>
        ),
        []
    );
    return (
        <StyledListItem ref={setNodeRef} style={style}>
            <Row flex={1} align="space-between" valign="center">
                <DragHandle className={classes.dragHandle} {...attributes} {...listeners} />
                {fields.map(field => (
                    <Typography
                        key={`sortable-item-${index}-${field.key}`}
                        style={{ flexBasis: field.width }}
                    >
                        {titleize((item[field.key] ?? '').replaceAll('_', ' '), true)}
                    </Typography>
                ))}
                <div style={{ flexBasis: '10%' }}>
                    <MenuOn items={menuItems} renderOn={renderOn} />
                </div>
            </Row>
        </StyledListItem>
    );
};
